import { useEffect, useRef } from 'react'
import { Campaign } from '@phrasee/phrasee-typings/Graphql/interfaces'
import { getESPTitle } from 'app/IntegrationDrawer'
import AcousticIntegration from 'workflow/CampaignSummary/IntegrationOptions/AcousticIntegration/AcousticIntegration'
import EpsilonIntegration from 'workflow/CampaignSummary/IntegrationOptions/EpsilonIntegration/EpsilonIntegration'
import EpsilonReactIntegration from 'workflow/CampaignSummary/IntegrationOptions/EpsilonIntegration/EpsilonReactIntegration'
import { shouldShowThePhraseeXIntegrationPanel } from 'workflow/CampaignSummary/IntegrationOptions/SfmcIntegration/helpers'
import SFMCIntegration from 'workflow/CampaignSummary/IntegrationOptions/SfmcIntegration/SfmcIntegrationManager'
import helpers from 'workflow/utils/helpers'

import Spinner from 'common/components/spinner'
import { useAppSelector } from 'common/hooks/redux'
import { isSFMC } from 'features/campaigns/components/details/helpers'
import PhraseeX from 'features/campaigns/integration-options/PhraseeX/PhraseeXIntegration'

const shouldShowPhraseeXIntegration = (campaignData: Campaign) => {
  const integrationType =
    campaignData?.campaign_configuration?.integration_options?.type || ''
  const isRealtimeApiEnabled =
    campaignData?.campaign_configuration?.realtime_api_enabled
  const { text_variants_accepted: textVariantsAccepted } =
    campaignData?.campaign_progress || {}
  const isBandit = helpers.isBanditCampaign(campaignData.campaign_configuration)

  return (
    (isRealtimeApiEnabled &&
      textVariantsAccepted &&
      !isSFMC(integrationType)) ||
    (textVariantsAccepted && !integrationType && isBandit)
  )
}

const OptimizeIntegrationSection = () => {
  const isSFMCErrorshown = useRef(false)

  const campaignData = useAppSelector(
    (state) => state.campaignStates.campaignData
  )
  const isLoadingCampaignData = useAppSelector(
    (state) => state.campaignStates.isLoadingCampaignDetails
  )

  const integrationType =
    campaignData?.campaign_configuration?.integration_options?.type

  const parseIntegrationType = (): string => {
    const selectionType =
      campaignData?.campaign_configuration?.testing_method?.optimization_method
        ?.selection_type

    const showPhraseeXIntegration = shouldShowPhraseeXIntegration(campaignData)
    if (showPhraseeXIntegration) {
      return 'phraseex'
    }

    if (selectionType === 'bandit' && integrationType === 'epsilon') {
      return 'epsilon_react'
    }

    if (isSFMC(integrationType)) {
      const isIframe = helpers.isAppInIFrame()

      if (!shouldShowThePhraseeXIntegrationPanel(campaignData) && !isIframe) {
        return ''
      }
    }

    return integrationType
  }

  const integrationTypeParsed = parseIntegrationType()

  const getIntegrationContent = () => {
    switch (integrationTypeParsed) {
      case 'sfmcv2':
      case 'salesforce':
        return <SFMCIntegration />
      case 'phraseex':
      case 'adobe_campaign_classic':
        return <PhraseeX />
      case 'epsilon':
        return <EpsilonIntegration />
      case 'epsilon_react':
        return <EpsilonReactIntegration />
      case 'acoustic':
        return <AcousticIntegration />
      default:
        return null
    }
  }

  const integrationContent = getIntegrationContent()
  const integrationTitle = getESPTitle(integrationTypeParsed)

  useEffect(() => {
    if (campaignData && !isLoadingCampaignData && !isSFMCErrorshown.current) {
      isSFMCErrorshown.current = true
      // errorToast('Access Jacquard via SFMC to continue.')
    }
  }, [campaignData, isLoadingCampaignData])

  if (!integrationContent && !isLoadingCampaignData) {
    return null
  }

  return (
    <div
      data-testid="optimizeIntegrationSection"
      className="bg-gold-100 h-full pt-6 pb-10 w-106"
    >
      {isLoadingCampaignData ? (
        <Spinner />
      ) : (
        <>
          <div className="px-9 flex justify-between">
            <div className="text-2xl font-medium text-coolGray-800">
              {integrationTitle}
            </div>
          </div>
          {integrationContent}
        </>
      )}
    </div>
  )
}

export default OptimizeIntegrationSection
